// Disclosure Guide Carousel (CodePen-inspired horizontal carousel)
import { domReady, initOnce } from '../../utils/dom.js';

function initDisclosureCarousel() {
  const section = document.querySelector('.disclosure-guides-section');
  if (!section) return;

  const cards = section.querySelectorAll('.card');
  const dots = section.querySelectorAll('.dot');
  const memberName = section.querySelector('.member-name');
  const memberRole = section.querySelector('.member-role');
  const guideInfoLink = section.querySelector('.guide-info-link');
  const instructionText = section.querySelector('.member-info p em');
  const leftArrow = section.querySelector('.nav-arrow.left');
  const rightArrow = section.querySelector('.nav-arrow.right');

  if (!cards.length || !window.disclosureGuides) {
    console.warn('Disclosure carousel: Missing cards or guide data');
    return;
  }

  const guides = window.disclosureGuides;
  let currentIndex = 0;
  let isAnimating = false;

  // Set dot aria-labels to guide names now that guides data is available
  dots.forEach((dot, i) => {
    if (guides[i]) {
      dot.setAttribute('aria-label', `Go to ${guides[i].name}`);
    }
  });

  function updateCarousel(newIndex) {
    if (isAnimating) return;
    isAnimating = true;

    currentIndex = (newIndex + cards.length) % cards.length;

    // Update card positions
    cards.forEach((card, i) => {
      const offset = (i - currentIndex + cards.length) % cards.length;

      card.classList.remove(
        'center',
        'left-1',
        'left-2',
        'right-1',
        'right-2',
        'hidden'
      );

      if (offset === 0) {
        card.classList.add('center');
      } else if (offset === 1) {
        card.classList.add('right-1');
      } else if (offset === 2) {
        card.classList.add('right-2');
      } else if (offset === cards.length - 1) {
        card.classList.add('left-1');
      } else if (offset === cards.length - 2) {
        card.classList.add('left-2');
      } else {
        card.classList.add('hidden');
      }
    });

    // Update dots
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === currentIndex);
    });

    // Fade out info
    if (memberName) memberName.style.opacity = '0';
    if (memberRole) memberRole.style.opacity = '0';
    if (guideInfoLink) guideInfoLink.style.opacity = '0';
    if (instructionText) instructionText.style.opacity = '0';

    // Update info with fade in
    setTimeout(() => {
      if (memberName) memberName.textContent = guides[currentIndex].name;
      if (memberRole) memberRole.textContent = guides[currentIndex].credentials || '';
      if (guideInfoLink) guideInfoLink.href = `/about/${guides[currentIndex].slug}`;
      if (instructionText) instructionText.textContent = `Click on image for information about ${guides[currentIndex].name}`;

      if (memberName) memberName.style.opacity = '1';
      if (memberRole) memberRole.style.opacity = '1';
      if (guideInfoLink) guideInfoLink.style.opacity = '1';
      if (instructionText) instructionText.style.opacity = '1';
    }, 300);

    setTimeout(() => {
      isAnimating = false;
    }, 800);
  }

  // Navigation arrows
  if (leftArrow) {
    leftArrow.addEventListener('click', () => {
      updateCarousel(currentIndex - 1);
    });
  }

  if (rightArrow) {
    rightArrow.addEventListener('click', () => {
      updateCarousel(currentIndex + 1);
    });
  }

  // Dot navigation
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      updateCarousel(i);
    });
  });

  // Card clicks and keyboard activation.
  // Cards are real <a href> links now (so Googlebot can crawl/follow them),
  // so only off-center cards need JS to intercept the click and recenter
  // instead of navigating; the center card's default link behavior does the rest.
  cards.forEach((card, i) => {
    card.addEventListener('click', (e) => {
      if (i !== currentIndex) {
        e.preventDefault();
        updateCarousel(i);
      }
    });
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (i !== currentIndex) {
          e.preventDefault();
          updateCarousel(i);
        } else if (e.key === ' ') {
          // Space doesn't trigger native <a> navigation like Enter does
          e.preventDefault();
          window.location.href = card.href;
        }
      }
    });
  });

  // Keyboard navigation
  const handleKeydown = (e) => {
    if (e.key === 'ArrowLeft') {
      updateCarousel(currentIndex - 1);
    } else if (e.key === 'ArrowRight') {
      updateCarousel(currentIndex + 1);
    }
  };
  document.addEventListener('keydown', handleKeydown);

  // Touch/swipe support
  let touchStartX = 0;
  let touchEndX = 0;

  const handleTouchStart = (e) => {
    touchStartX = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX = e.changedTouches[0].screenX;
    const swipeThreshold = 50;
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        updateCarousel(currentIndex + 1);
      } else {
        updateCarousel(currentIndex - 1);
      }
    }
  };

  document.addEventListener('touchstart', handleTouchStart);
  document.addEventListener('touchend', handleTouchEnd);

  // Initialize carousel at index 0
  updateCarousel(0);
}

domReady(initDisclosureCarousel);
